import React, { useState, useEffect, useRef } from 'react'
import { version } from '../../../../package.json'
import { ApolloProvider, ApolloClient } from '@apollo/client'
import link from './link'
import { InMemoryCache, defaultDataIdFromObject } from '@apollo/client'
import { persistCache, CachePersistor } from 'apollo3-cache-persist'
import { apolloStorage } from 'config/storage'
import { schema as typeDefs, resolvers } from 'config/localServer'
import { InitialLoading } from 'components/Loading'
import { Auth, Cache } from 'aws-amplify'
import get from 'lodash/get'

const CURRENT_APOLLO_CLIENT_CACHE_VERSION = 2

const withProvider = Component => {
	const Provider = props => {
		const apolloClient = useRef()
		const cachePersistor = useRef()
		const [providerReady, setProviderReady] = useState(false)
		const [companyRefreshed, setCompanyRefreshed] = useState(false)

		const resetCache = async () => {
			if (cachePersistor.current) {
				cachePersistor.current.pause() // Pause automatic persistence.
				await cachePersistor.current.purge() // Delete everything in the storage provider.
			}
			await apolloClient.current.clearStore()	//clear apollo-client store
			Cache.clear()	//clears aws-amplify cache
		}

		useEffect(() => {
			const shouldPersistCache = !JSON.parse(sessionStorage.getItem('useSessionStorage'))
			const apolloCacheVersion = JSON.parse(localStorage.getItem('apolloCacheVersion')) || 0
			const cache = new InMemoryCache({
				dataIdFromObject: object => {
					if(object.__typename === 'Img') {
						return `Img:${object.uid}`
					}
					if(object.__typename === 'SetFixedProduct') {
						return `${object.__typename}:${object.code}`
					}
					if(object.__typename === 'AndroidDevice') {
						return `${object.__typename}:${object.hardwareId}`
					}
					if(
						object.__typename === 'CA_Query' ||
						object.__typename === 'CashRegisterGlobalParameters' ||
						object.__typename === 'AppConfig' ||
						object.__typename === 'VirtualCardConfig' ||
						object.__typename === 'AppConfigAdditional'
					) {
						return object.__typename
					}

					// if(typeof object.id === 'undefined') {
					// 	return object.__typename
					// }
					return defaultDataIdFromObject(object)
				},
				typePolicies: {
					Query: {
						fields: {
							cashAssist: {
								// This ignores incomming { cashAssist: null } object on request error
								// to prevent whole cache invalidation (this needs to be fixed on graphql server)
								merge(existing, incomming, { mergeObjects }) {
									if(incomming === null) {
										return existing
									}
									return mergeObjects(existing, incomming)
								}
							}
						}
					}
				}
			})
			const clientParams = {
				link,
				version,
				cache: cache,
				connectToDevTools: true,
				name: `cashassist-web${process.env.NODE_ENV === 'production' ? '' : '-dev'}`,
				typeDefs,
				resolvers
			}
			if (shouldPersistCache) {
				const persistantCacheParams = {
					cache: cache,
					maxSize: false,
					storage: apolloStorage
				}
				cachePersistor.current = new CachePersistor(persistantCacheParams)
				persistCache(persistantCacheParams).then((smth) => {
					apolloClient.current = new ApolloClient(clientParams)
					if(apolloCacheVersion < CURRENT_APOLLO_CLIENT_CACHE_VERSION) {
						apolloClient.current.clearStore().then(() => {
							localStorage.setItem('apolloCacheVersion', CURRENT_APOLLO_CLIENT_CACHE_VERSION)
						})
					}
					setProviderReady(true)
				})
			} else {
				apolloClient.current = new ApolloClient(clientParams)
				setProviderReady(true)
			}
		}, [])

		useEffect(() => {
			const refreshCompany = async () => {
				const user = await Auth.currentAuthenticatedUser()
				const accessRights = JSON.parse(get(user, 'attributes.custom:access_rights', '{}'))
				if (accessRights.chc_sa) {
					const oldCompany = localStorage.getItem('chc_sa.companyId')
					const refreshToken = (await Auth.currentSession()).getRefreshToken()
					user.refreshSession(refreshToken, async (err, session) => {
						const newCompany = session.getIdToken().payload['custom:company']
						if (!session || oldCompany !== newCompany) {
							await resetCache()
							localStorage.setItem('chc_sa.companyId', newCompany)
						}
						setCompanyRefreshed(true)
					})
				} else {
					setCompanyRefreshed(true)
				}
			}
			refreshCompany()
		}, [])

		return providerReady && companyRefreshed ? (
			<ApolloProvider client={apolloClient.current}>
				<Component {...props} resetCache={resetCache}/>
			</ApolloProvider>
		) : <InitialLoading />
	}
	return Provider
}
export default withProvider
